import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
} from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import { useCallback, useEffect, useRef } from 'react';
import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  HTMLRenderer,
  MixedStyleDeclaration,
} from 'shared/components/HTMLRenderer/HTMLRenderer';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useAppLifecycle } from 'shared/hooks/useAppLifecycle/useAppLifecycle';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';
import { showZendeskArticle } from 'shared/utils/zendesk/zendeskUtils';

const CURRENT_APY_ITEM_KEY = 'apy';

const subheadingStyle: MixedStyleDeclaration = {
  fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
  fontWeight: 'normal',
  fontSize: theme.typography.title.md.fontSize,
  lineHeight: theme.typography.title.md.lineHeight,
  letterSpacing: theme.typography.title.md.letterSpacing,
  marginTop: theme.spacing.m,
  marginBottom: theme.spacing.s,
};

const apyTagStyles: Record<string, MixedStyleDeclaration> = {
  b: subheadingStyle,
  strong: subheadingStyle,
  p: {
    ...(theme.typography.body.base as MixedStyleDeclaration),
    marginTop: 0,
    marginBottom: theme.spacing.m,
  },
  ul: {
    marginTop: -theme.spacing.xs,
    marginBottom: theme.spacing.m,
    paddingLeft: theme.spacing.l,
  },
  li: {
    ...(theme.typography.body.base as MixedStyleDeclaration),
    marginTop: 0,
    marginBottom: theme.spacing.s,
  },
};

const apyRenderersProps = {
  ul: {
    markerBoxStyle: {
      paddingRight: theme.spacing.s,
    },
  },
};

const CurrentAPY = () => {
  const { id: accountId } = useLocalSearchParams<{ id: string }>();
  const { accountDetails, errorModel, loading, refetch } =
    useAccountDetails(accountId);
  const { lastResumeAt } = useAppLifecycle();
  const hasFocusedOnce = useRef(false);
  const { width } = useWindowDimensions();
  const handleFAQClickedAnalytics = usePressableAnalytics({
    analyticsId: 'faqClicked',
    customAttributes: {
      faq_name: 'current_apy_qualification',
    },
  });

  useFocusEffect(
    // useFocusEffect re-runs whenever the callback identity changes, so an unstable callback refetches on every render.
    // eslint-disable-next-line custom/no-manual-react-memoization
    useCallback(() => {
      if (hasFocusedOnce.current) {
        refetch();
      }
      hasFocusedOnce.current = true;
    }, [refetch]),
  );

  useEffect(() => {
    if (lastResumeAt) {
      refetch();
    }
  }, [lastResumeAt, refetch]);

  const apyData = accountDetails?.details
    ?.flatMap((group) => group.items)
    .find((item) => item.key === CURRENT_APY_ITEM_KEY);

  const onPressHyperLink = () => {
    handleFAQClickedAnalytics();
    try {
      if (apyData?.itemDetail?.articleLink?.articleId) {
        showZendeskArticle(
          parseInt(apyData.itemDetail.articleLink.articleId, 10),
        );
      }
    } catch (error) {
      // TODO/JT-120079: Need to add analytics/error-logging service
      console.error('Failed to open APY Zendesk article:', error);
    }
  };

  const contentWidth = width - theme.spacing.m * 2;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
      >
        <TitleLgText numberOfLines={1} style={styles.title}>
          {apyData?.itemDetail?.title}
        </TitleLgText>
        <HTMLRenderer
          contentWidth={contentWidth}
          htmlAsString={apyData?.itemDetail?.descriptionHtml ?? ''}
          tagsStyles={apyTagStyles}
          renderersProps={apyRenderersProps}
        />
        <HyperlinkBaseText style={styles.link} onPress={onPressHyperLink}>
          {apyData?.itemDetail?.articleLink?.copy ?? ''}
        </HyperlinkBaseText>
      </ScrollView>
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.m,
    paddingBottom: theme.spacing.xl,
  },
  title: {
    marginBottom: theme.spacing.m,
  },
  link: {
    marginTop: 0,
  },
});

export default CurrentAPY;
